import Cookies from 'js-cookie';
import { AUTH_COOKIE, TWO_FACTOR_COOKIE } from '../../constants/sessionCookies';
import {
  loginUser as loginUserFromServer,
  forgotPassword as forgotPasswordFromServer,
  resetPassword as resetPasswordFromServer,
  logoutUser as logoutUserFromServer,
  getUser as getUserFromServer,
  verifyMultiFactor as verifyMultiFactorFromServer,
  confirmMultiFactor as confirmMultiFactorFromServer
} from '../../api';
import { setAlert, setSuccess, clearAlerts, clearErrors, AlertSeverity } from './alertActions';
import { clearAnnouncements } from './announcementActions';
import { clearAnnouncedEmergencyCall } from './statusActions';
import { checkSetupMode } from './setupModeActions';

import { isNull } from '../../constants';

export const IS_AUTH_LOADING = 'IS_AUTH_LOADING';
export const IS_AUTH_NOT_LOADING = 'IS_AUTH_NOT_LOADING';
export const INITIAL_LOGIN_SUCCESS = 'INITIAL_LOGIN_SUCCESS';
export const INITIAL_FORGOT_PASSWORD_SUCCESS = 'INITIAL_FORGOT_PASSWORD_SUCCESS';
export const NOT_AUTHENTICATED = 'NOT_AUTHENTICATED';
export const NOT_TWO_FACTORED_AUTHENTICATED = 'NOT_TWO_FACTORED_AUTHENTICATED';
export const AUTHENTICATED_SUCCESS = 'AUTHENTICATED_SUCCESS';
export const PASSWORD_UPDATED = 'PASSWORD_UPDATED';

export const startLoading = () => ({
  type: IS_AUTH_LOADING
});

export const stopLoading = () => ({
  type: IS_AUTH_NOT_LOADING
});

export const initialLoginSuccess = (userID, userType, passwordResetRequired, multiFactorSetupRequired) => ({
  type: INITIAL_LOGIN_SUCCESS,
  userID,
  userType,
  passwordResetRequired,
  multiFactorSetupRequired
});

export const authenticatedSuccess = (userID = null, userType = null, passwordResetRequired = null, multiFactorSetupRequired = null) => ({
  type: AUTHENTICATED_SUCCESS,
  userID,
  userType,
  passwordResetRequired,
  multiFactorSetupRequired
});

export const notTwoFactoredAuthenticated = () => ({
  type: NOT_TWO_FACTORED_AUTHENTICATED
});

export const notAuthenticated = () => ({
  type: NOT_AUTHENTICATED
});

export const passwordUpdated = () => ({
  type: PASSWORD_UPDATED
});

const handleMobileChecking = (dispatch, mobileAccess) => {
  if (window && window.screen && window.screen.width <= 768 && !mobileAccess) {
    setAlert(dispatch, 'You do not have mobile access.');
    logoutUser(dispatch);
  }
};

export const checkAuthentication = (dispatch, userID, isAuthenticated) => {
  dispatch(startLoading());

  // If cookies don't exist but we have user data saved
  if ((isNull(Cookies.get(AUTH_COOKIE)) && !isNull(userID)) || (isNull(Cookies.get(TWO_FACTOR_COOKIE)) && !isNull(isAuthenticated))) {
    // Log out user entirely
    dispatch(notAuthenticated());
    Cookies.remove(AUTH_COOKIE);
    Cookies.remove(TWO_FACTOR_COOKIE);
  }

  // If cookie exists but we don't have the right user information saved in redux state
  else if ((Cookies.get(AUTH_COOKIE) && isNull(userID)) || (Cookies.get(TWO_FACTOR_COOKIE) && !isAuthenticated)) {
    // Fill in the user info
    getUserFromServer().then(user => {
      handleMobileChecking(dispatch, user.mobileAccess);
      dispatch(authenticatedSuccess(user.id, user.userType, user.passwordResetRequired, user.multiFactorSetupRequired));
    }).catch(() => {
      logoutUser(dispatch);
    });
  }

  else {
    dispatch(stopLoading());
  }
};

export const loginUser = (dispatch, email = null, password = null) => {
  dispatch(startLoading());

  // Attempt to log in the user
  loginUserFromServer(email, password)
    .then((user) => {
      dispatch(clearErrors());
      dispatch(initialLoginSuccess(user.id, user.userType, user.passwordResetRequired, user.multiFactorSetupRequired));
    })
    .catch((error) => {
      setAlert(
        dispatch,
        'Error: ' + error && error.response && error.response.data
          ? error.response.data
          : 'Could not log in.'
      );
      dispatch(notAuthenticated());
    });
};
export const forgotPassword = (dispatch, email = null) => {
  dispatch(startLoading());

  // The server always responds the same way whether or not the email is registered,
  // so this only ever reports success back to the user.
  forgotPasswordFromServer(email)
    .then(() => {
      dispatch(clearErrors());
      setSuccess(dispatch, 'If that email is registered, a password reset link has been sent to it.');
      dispatch(stopLoading());
    })
    .catch((error) => {
      setAlert(
        dispatch,
        'Error: ' + error && error.response && error.response.data
          ? error.response.data
          : 'Could not send the password reset email.'
      );
      dispatch(stopLoading());
    });
};

export const resetPassword = (dispatch, token, password) => {
  dispatch(startLoading());

  return resetPasswordFromServer(token, password)
    .then(() => {
      dispatch(clearErrors());
      dispatch(stopLoading());
      return true;
    })
    .catch((error) => {
      setAlert(
        dispatch,
        'Error: ' + error && error.response && error.response.data
          ? error.response.data
          : 'Could not reset your password. The link may have expired.'
      );
      dispatch(stopLoading());
      return false;
    });
};

export const authenticateUser = (dispatch, token) => {
  dispatch(startLoading());

  // Attempt to 2-factor authenticate the user
  verifyMultiFactorFromServer(token).then(() => {
    dispatch(clearErrors());
    dispatch(authenticatedSuccess());
    checkSetupMode(dispatch);
  }).catch(error => {
    setAlert(
      dispatch,
      'Error: ' + error && error.response && error.response.data
        ? error.response.data
        : 'Could not log in.'
    );
    dispatch(notTwoFactoredAuthenticated());
  });
};

export const logoutUser = (dispatch) => {
  // Revoke tokens and unauthenticate in redux state
  Cookies.remove(AUTH_COOKIE);
  Cookies.remove(TWO_FACTOR_COOKIE);
  dispatch(notAuthenticated());

  // Announcements outlive a session otherwise: they are deliberately
  // persistent, so an emergency call banner raised while logged in would still
  // be sitting on the login screen afterwards, describing an incident the
  // logged-out viewer has no context for.
  dispatch(clearAnnouncements());
  clearAnnouncedEmergencyCall();

  // Logout officially from server
  logoutUserFromServer()
    .then(() => {
      dispatch(clearAlerts());
      setAlert(dispatch, 'Log out successful', AlertSeverity.SUCCESS, true);
    })
    .catch((error) => {
      console.log(error);
    });
};

export const forceLogoutUser = () => {
  // Revoke tokens
  Cookies.remove(AUTH_COOKIE);
  Cookies.remove(TWO_FACTOR_COOKIE);
  window.location.reload(false); // Hopefully we can do this more gracefully in the future.
};

export const submitSetupTwoFactorAuthentication = (dispatch, token) => {
  dispatch(startLoading());

  confirmMultiFactorFromServer(token).then(() => {
    dispatch(clearErrors());
    dispatch(authenticatedSuccess());
    setAlert(
      dispatch,
      'Successfully set up 2FA!',
      AlertSeverity.SUCCESS,
      true
    );
  }).catch(error => {
    setAlert(
      dispatch,
      'Error: ' + error && error.response && error.response.data
        ? error.response.data
        : 'Could not log in.'
    );
    dispatch(notTwoFactoredAuthenticated());
  });
};
